import React from 'react';
import { Box, Avatar, Typography, Chip, Tooltip } from '@mui/material';
import { useAuth } from '@/hooks/useAuth';
import { filterEquipRoles, getRoleDisplayLabel } from '@equip/utils';

interface UserProfileProps {
  showRoles?: boolean;
  variant?: 'compact' | 'full';
  collapsed?: boolean;
}

export const UserProfile: React.FC<UserProfileProps> = ({
  showRoles = true,
  variant = 'compact',
  collapsed = false,
}) => {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  const displayName =
    user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username;

  const initials =
    user.firstName && user.lastName
      ? `${user.firstName[0]}${user.lastName[0]}`
      : user.username.substring(0, 2).toUpperCase();

  const equipRoles = filterEquipRoles(user.roles);

  const tooltipContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 0.5, pr: 1.5 }}>
      <Typography variant="subtitle2">{displayName}</Typography>
      {equipRoles.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {equipRoles.map((role) => (
            <Chip
              key={role}
              label={getRoleDisplayLabel(role)}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem', height: 20, color: 'inherit', borderColor: 'rgba(255,255,255,0.4)' }}
            />
          ))}
        </Box>
      )}
      <Typography variant="body2" sx={{ opacity: 0.8, whiteSpace: 'nowrap' }}>
        {user.email || user.username}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Tooltip title={tooltipContent} placement="right" arrow slotProps={{ tooltip: { sx: { maxWidth: 'none' } } }}>
          <Avatar sx={{ bgcolor: 'primary.main', cursor: 'default' }}>{initials}</Avatar>
        </Tooltip>
        {!collapsed && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {displayName}
            </Typography>
            {showRoles && equipRoles.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {equipRoles.map((role) => (
                  <Chip
                    key={role}
                    label={getRoleDisplayLabel(role)}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem', height: 20 }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {!collapsed && variant === 'full' && (
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          title={user.email || user.username}
          sx={{ cursor: 'default' }}
        >
          {user.email || user.username}
        </Typography>
      )}
    </Box>
  );
};
